import { createFileRoute, useRouter } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { useMutation } from '@apollo/client/react';
import { ArrowLeftIcon, ArrowRightIcon } from '@heroicons/react/24/outline';
import z from 'zod';
import { Button } from '@sc/shared-ui';
import { GQL_MUTATION_CONFIRM_EMAIL } from '../utility/queries';
import { useAuthenticated } from '../providers/authenticated';
import { notify } from '../utility/notify';
import { track } from '../utility/analytics';
import {
  AuthLayout,
  AuthSupportLink,
  AuthTitle,
} from '../components/AuthLayout';

const verifyEmailSearchSchema = z.object({
  verifyEmail: z.string().optional(),
});

const INVALID_LINK =
  'This verification link is invalid or has expired. Please request a new one.';

type ConfirmInputType = { email: string; verification: string };

const parseLink = (verifyEmail?: string): ConfirmInputType | undefined => {
  try {
    const [verificationBase64, emailBase64] = verifyEmail
      ? decodeURIComponent(verifyEmail).split('.')
      : [];

    if (!verificationBase64 || !emailBase64) return undefined;

    return {
      verification: atob(verificationBase64),
      email: atob(emailBase64),
    };
  } catch {
    return undefined;
  }
};

// The token is single-use, so it must only be redeemed on an explicit click:
// email security scanners render the page (and run its JS) before the user
// ever sees it, and would otherwise consume the link on their behalf.
const VerifyEmail = () => {
  const router = useRouter();
  const { verifyEmail } = Route.useSearch();
  const { setIsAuthenticated } = useAuthenticated();

  const [hasFailed, setHasFailed] = useState(false);

  const input = useMemo(() => parseLink(verifyEmail), [verifyEmail]);

  const [confirmEmail, { loading: isConfirming }] = useMutation(GQL_MUTATION_CONFIRM_EMAIL, {
    onError: () => {
      notify.error(INVALID_LINK);

      setHasFailed(true);
    },
    onCompleted: () => {
      track('Sign Up Completed', { method: 'email' });

      notify.success('Email verified.');

      const authDate = new Date().getTime().toString();

      setIsAuthenticated(authDate);

      router.options.context.isAuthenticated = authDate;

      void router.navigate({ to: '/kb', hash: 'create' });
    },
  });

  const toLogin = () => void router.navigate({ to: '/login' });

  if (!input || hasFailed)
    return (
      <AuthLayout showLockUp footer={<AuthSupportLink />}>
        <AuthTitle subtitle={INVALID_LINK}>Invalid or expired link</AuthTitle>

        <Button
          button={{
            text: 'Back to login',
            type: 'basic',
            IconStart: ArrowLeftIcon,
            size: 'large',
            onClick: toLogin,
          }}
        />
      </AuthLayout>
    );

  return (
    <AuthLayout showLockUp loading={isConfirming}>
      <AuthTitle subtitle="Click below to confirm your email address and continue to your account.">
        Verify your email
      </AuthTitle>

      <Button
        button={{
          text: 'Verify email',
          type: 'primary',
          IconEnd: ArrowRightIcon,
          size: 'large',
          loading: isConfirming,
          onClick: () => void confirmEmail({ variables: { input } }),
        }}
      />
    </AuthLayout>
  );
};

export const Route = createFileRoute('/verify-email')({
  component: VerifyEmail,
  validateSearch: (search) => verifyEmailSearchSchema.parse(search),
});
